/**
 * Manages document lifecycle, metadata, and recent documents
 *
 * Responsibilities:
 * - Handle document loading and state updates
 * - Manage document metadata and statistics
 * - Handle recent documents selection
 * - Manage file handles for File System Access API
 * - Reprocess documents with new language settings
 *
 * Dependencies:
 * - DocumentHistory: For recent document tracking
 * - FileHandleManager: For file handle persistence
 * - StatusBar: For document stats display
 * - LanguageSelector: For language state updates
 * - UIManager: For translations
 * - DocumentParser: For document parsing
 *
 * State Management:
 * - Updates useAppStore for centralized state
 * - Also maintains local state for backward compatibility
 */

import type DocumentHistory from './document-history';
import type { DocumentEntry, DocumentSource } from './document-history';
import type FileHandleManager from './file-handle-manager';
import { useAppStore } from '../stores/app-store';
import { useUIStore } from '../stores/ui-store';
import type DocumentParser from './document-parser';
import type { ParsedDocument } from './document-parser';
import type { LanguageCode } from './language-constants';
import { escapeHtml } from './utilities/dom';
import { extractDocumentText } from './utilities/document';
import ToastService from '../components/toast-service';
import { logger } from './logger';
import { forbiddenMessage } from './forbidden-message';

// Document type with all metadata
export interface Document {
  name: string;
  content: string;
  size: number;
  type: string;
  lastModified: number;
  parsed: ParsedDocument;
  /** #98 part 4: a stored analysis remembers where it came from. */
  source?: DocumentSource;
}

import type { IUIManager } from '../i18n/ui-manager.types';
import type { DocumentStats } from '../components/status-bar';

// What DocumentManager needs from the StatusBar (tests pass small fakes)
export interface StatusBar {
  setDocumentState(state: string): void;
  setDocumentStats(stats: DocumentStats): void;
}

// What DocumentManager needs from the LanguageSelector
export interface LanguageSelector {
  setDocumentLoaded(loaded: boolean): void;
  setCurrentLanguage(languageCode: LanguageCode, autoDetected: boolean): void;
}

// What DocumentManager needs from RecentDocuments
export interface RecentDocuments {
  updateHistory(): void;
}

// FileLoader interface (minimal)
export interface FileLoader {
  loadFile(file: File): Promise<void>;
  /** Open the drop area's file picker; false when none is available. */
  openFilePicker(): boolean;
}

// Options for DocumentManager constructor
export interface DocumentManagerOptions {
  documentHistory: DocumentHistory;
  fileHandleManager: FileHandleManager;
  statusBar: StatusBar;
  languageSelector: LanguageSelector;
  recentDocuments: RecentDocuments;
  uiManager: IUIManager;
  parser: DocumentParser;
  onDocumentLoaded?: ((document: Document) => void) | null;
  displayDocument?: ((document: Document) => void) | null;
  updateEnhancementMenu?: (() => void) | null;
  setStatus?: ((message: string, type: string) => void) | null;
  setLoading?: ((loading: boolean) => void) | null;
  buildDocumentInfoHTML?:
    | ((document: Document, languageName: string, autoText: string) => string)
    | null;
}

class DocumentManager {
  // Dependencies
  private documentHistory: DocumentHistory;
  private fileHandleManager: FileHandleManager;
  private statusBar: StatusBar;
  private languageSelector: LanguageSelector;
  private recentDocuments: RecentDocuments;
  private uiManager: IUIManager;
  private parser: DocumentParser;

  // State
  private currentDocument: Document | null;
  private currentFileHandle: FileSystemFileHandle | null;
  private currentLanguage: LanguageCode;

  // Callbacks
  private onDocumentLoaded: ((document: Document) => void) | null;
  /** #98 part 4: reopens a recent entry that has a source (set by the web app). */
  reopenSource: ((entry: DocumentEntry) => Promise<void>) | null = null;
  private displayDocument: ((document: Document) => void) | null;
  private updateEnhancementMenu: (() => void) | null;
  private setStatus: ((message: string, type: string) => void) | null;
  private setLoading: ((loading: boolean) => void) | null;
  private buildDocumentInfoHTML:
    | ((document: Document, languageName: string, autoText: string) => string)
    | null;

  /**
   * @param options - Configuration options
   */
  constructor({
    documentHistory,
    fileHandleManager,
    statusBar,
    languageSelector,
    recentDocuments,
    uiManager,
    parser,
    onDocumentLoaded = null,
    displayDocument = null,
    updateEnhancementMenu = null,
    setStatus = null,
    setLoading = null,
    buildDocumentInfoHTML = null,
  }: DocumentManagerOptions) {
    // Dependencies
    this.documentHistory = documentHistory;
    this.fileHandleManager = fileHandleManager;
    this.statusBar = statusBar;
    this.languageSelector = languageSelector;
    this.recentDocuments = recentDocuments;
    this.uiManager = uiManager;
    this.parser = parser;

    // State
    this.currentDocument = null;
    this.currentFileHandle = null;
    this.currentLanguage = parser ? parser.getCurrentLanguage() : 'en';

    // Callbacks
    this.onDocumentLoaded = onDocumentLoaded;
    this.displayDocument = displayDocument;
    this.updateEnhancementMenu = updateEnhancementMenu;
    this.setStatus = setStatus;
    this.setLoading = setLoading;
    this.buildDocumentInfoHTML = buildDocumentInfoHTML;
  }

  /**
   * Handle successful document loading
   * @param document - Loaded document object
   * @param fileHandle - File handle (optional)
   */
  handleDocumentLoaded(
    document: Document,
    fileHandle: FileSystemFileHandle | null = null
  ): void {
    logger.info('Document loaded:', document);
    this.currentDocument = document;

    // Update centralized store
    useAppStore.getState().setDocument(document);

    // Store file handle if provided
    if (fileHandle) {
      this.currentFileHandle = fileHandle;
    }

    // Add to document history
    if (this.documentHistory) {
      const docEntry = this.documentHistory.addDocument({
        name: document.name,
        size: document.size,
        type: document.type,
        lastModified: document.lastModified,
        ...(document.source ? { source: document.source } : {}),
      });

      // Store file handle for this document if available
      if (fileHandle && docEntry && this.fileHandleManager) {
        this.fileHandleManager.setFileHandle(docEntry.id, fileHandle);
        logger.debug('Stored file handle for:', docEntry.id);
      }

      // Update recent documents UI
      if (this.recentDocuments) {
        this.recentDocuments.updateHistory();
      }
    }

    // Update document loading state in store (StatusBar will subscribe to this)
    useUIStore.getState().setDocumentLoadingState('loaded');

    // Update status bar to show document loaded (legacy - will be deprecated)
    if (this.statusBar) {
      this.statusBar.setDocumentState('loaded');
    }

    // Update language selector to show document is loaded
    if (this.languageSelector) {
      this.languageSelector.setDocumentLoaded(true);
    }

    // Update language selector based on detected language
    const detectedLanguage = document.parsed.metadata.language;
    const wasAutoDetected =
      document.parsed.metadata.detectedLanguage !== this.currentLanguage;

    if (detectedLanguage && detectedLanguage !== this.currentLanguage) {
      this.currentLanguage = detectedLanguage;

      if (this.languageSelector) {
        this.languageSelector.setCurrentLanguage(
          detectedLanguage,
          wasAutoDetected
        );
      }

      // DO NOT refresh topics here - topics use UI language, not document language
      // Topics remain in the interface language regardless of document language
    }

    // Call display callback
    if (this.displayDocument) {
      this.displayDocument(document);
    }

    // Update document info
    this.updateDocumentInfo(document);

    // Update enhancement menu
    if (this.updateEnhancementMenu) {
      this.updateEnhancementMenu();
    }

    // Emit callback
    if (this.onDocumentLoaded) {
      this.onDocumentLoaded(document);
    }
  }

  /**
   * Handle document selected from recent documents list
   * @param docMetadata - Document metadata from history
   * @param fileLoader - File loader instance
   */
  async handleRecentDocumentSelect(
    docMetadata: DocumentEntry,
    fileLoader: FileLoader
  ): Promise<void> {
    logger.info('Recent document selected:', docMetadata.name);

    // #98 part 4: a stored analysis is fetched again, not read from a file.
    if (docMetadata.source && this.reopenSource) {
      await this.reopenSource(docMetadata);
      return;
    }

    // Check if we have a file handle for this document
    if (
      this.fileHandleManager &&
      this.fileHandleManager.hasFileHandle(docMetadata.id)
    ) {
      try {
        // Try to reopen the file using the stored file handle
        logger.debug('Attempting to reopen file using stored handle...');
        const file = await this.fileHandleManager.getFile(docMetadata.id);

        if (file) {
          // Successfully retrieved the file - now load it
          logger.debug('File retrieved successfully:', file.name);

          // Get the file handle to pass along
          const fileHandle = this.fileHandleManager.getFileHandle(
            docMetadata.id
          );

          // Load the file using the file loader
          if (fileLoader && typeof fileLoader.loadFile === 'function') {
            await fileLoader.loadFile(file);

            // Update the file handle after successful load
            if (fileHandle && this.currentDocument) {
              this.currentFileHandle = fileHandle;
            }
          } else {
            throw new Error('File loader not available');
          }

          return; // Success - exit early
        } else {
          logger.warn(
            'Could not retrieve file - may have been moved or deleted'
          );
          // Fall through to show error message
        }
      } catch (error) {
        logger.error('Error reopening file:', error);

        // Show specific error message if permission was denied
        if ((error as any).name === 'NotAllowedError') {
          const message =
            this.uiManager.t('recentDocs.permissionDenied', {
              filename: docMetadata.name,
            }) ||
            `Permission denied to access '${docMetadata.name}'. Please load the file again.`;

          ToastService.getInstance().showError(message, {
            title: 'Document Manager',
          });
          return;
        }
        // Fall through to show general error message
      }
    }

    // File handle not available (Firefox/Safari or file from previous session)
    // Open the file picker to let the user reselect the file
    logger.debug('No file handle available - opening file picker');
    if (fileLoader?.openFilePicker()) return;

    const message = this.uiManager.t('recentDocs.cannotReopen', {
      filename: docMetadata.name,
    });
    const displayMessage =
      message ?? `Please load the file '${docMetadata.name}' again.`;
    ToastService.getInstance().showInfo(displayMessage, {
      title: 'Document Manager',
    });
  }

  /**
   * Update document information display
   * @param document - Document object
   * @param documentInfoElement - Document info element (optional)
   */
  updateDocumentInfo(
    document: Document,
    documentInfoElement: HTMLElement | null = null
  ): void {
    const metadata = document.parsed.metadata;

    // Update status bar with document statistics
    if (this.statusBar) {
      this.statusBar.setDocumentStats({
        filename: document.name,
        words: metadata.wordCount,
        sentences: metadata.sentenceCount,
        paragraphs: metadata.paragraphCount,
        characters: metadata.characterCount,
        parseTime: metadata.parseTime,
        fileSize: document.size,
      });
    }

    // Update sidebar document info element if provided
    const infoElement = documentInfoElement;
    if (infoElement) {
      const languageName =
        metadata.languageName || this.parser.getLanguageDisplayName();
      const wasDetected = metadata.detectedLanguage !== metadata.language;
      const autoText = wasDetected
        ? `(${this.uiManager.t('documentInfo.autoDetected')})`
        : '';

      // Environment-specific document info display
      if (this.buildDocumentInfoHTML) {
        infoElement.innerHTML = this.buildDocumentInfoHTML(
          document,
          languageName,
          autoText
        );
      } else {
        // Fallback basic info
        infoElement.innerHTML = `
          <strong>${escapeHtml(document.name)}</strong><br>
          ${this.uiManager.t('documentInfo.language')}: ${escapeHtml(languageName)} ${escapeHtml(autoText)}<br>
          ${this.uiManager.t('documentInfo.words')}: ${metadata.wordCount}<br>
          ${this.uiManager.t('documentInfo.sentences')}: ${metadata.sentenceCount}<br>
          ${this.uiManager.t('documentInfo.paragraphs')}: ${metadata.paragraphCount}<br>
          ${this.uiManager.t('documentInfo.characters')}: ${metadata.characterCount}
        `;
      }
    }
  }

  /**
   * Re-process current document with current language settings
   */
  async reprocessDocument(): Promise<void> {
    if (!this.currentDocument) return;

    try {
      if (this.setStatus) {
        this.setStatus(
          this.uiManager.t('status.reprocessing') || 'Reprocessing...',
          'loading'
        );
      }
      if (this.setLoading) {
        this.setLoading(true);
      }

      logger.debug('Re-processing document with current language settings...');

      // Re-parse the document content
      const reparsedData = await this.parser.parseDocument(
        this.currentDocument.content
      );

      // Update document with new parsed data
      this.currentDocument.parsed = reparsedData;

      // Update language selector if language was auto-detected
      if (reparsedData.metadata.detectedLanguage !== this.currentLanguage) {
        this.currentLanguage = reparsedData.metadata.language;
        this.languageSelector?.setCurrentLanguage(this.currentLanguage, true);
      }

      // Redisplay the document
      if (this.displayDocument) {
        this.displayDocument(this.currentDocument);
      }

      // Update document info
      this.updateDocumentInfo(this.currentDocument);

      if (this.setStatus) {
        this.setStatus(
          this.uiManager.t('status.reprocessed') || 'Reprocessed',
          'info'
        );
      }
      if (this.setLoading) {
        this.setLoading(false);
      }
    } catch (error) {
      logger.error('Error re-processing document:', error);
      const errorMsg =
        forbiddenMessage(error, (k) => this.uiManager.t(k)) ??
        `${this.uiManager.t('errors.reprocessingFailed')}: ${(error as Error).message}`;

      if (this.setStatus) {
        this.setStatus(errorMsg, 'error');
      } else {
        ToastService.getInstance().showError(errorMsg, {
          title: 'Document Manager',
        });
      }

      if (this.setLoading) {
        this.setLoading(false);
      }

      throw error;
    }
  }

  /**
   * Get full text content from current document
   * @returns Document text
   */
  getDocumentText(): string {
    if (!this.currentDocument || !this.currentDocument.parsed) {
      return '';
    }
    return extractDocumentText(this.currentDocument.parsed);
  }

  /**
   * Get current document
   * @returns Current document
   */
  getCurrentDocument(): Document | null {
    return this.currentDocument;
  }

  /**
   * Get current file handle
   * @returns Current file handle
   */
  getCurrentFileHandle(): FileSystemFileHandle | null {
    return this.currentFileHandle;
  }

  /**
   * Get current language
   * @returns Current language code
   */
  getCurrentLanguage(): LanguageCode {
    return this.currentLanguage;
  }

  /**
   * Set current language
   * @param languageCode - Language code
   */
  setCurrentLanguage(languageCode: LanguageCode): void {
    this.currentLanguage = languageCode;
  }

  /**
   * Set document loading state in the store
   * Call this when starting to load a document
   * @param state - Loading state ('none' | 'loading' | 'loaded')
   */
  setDocumentLoadingState(state: 'none' | 'loading' | 'loaded'): void {
    useUIStore.getState().setDocumentLoadingState(state);

    // Also update status bar directly for backward compatibility
    if (this.statusBar) {
      this.statusBar.setDocumentState(state);
    }
  }
}

export default DocumentManager;
